import { SidebarTrigger } from '@glade-ai/ui';
import type { Icon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { type ReactNode, useRef } from 'react';

import Buttons from '@/components/Buttons';
import CaretCircleDown from '@/components/Icons/CaretCircleDown';
import NoodleAI from '@/components/Icons/NoodleAI';
import removeFalseyExcept0 from '@/helpers/removeFalseyExcept0';

import Switch from '../Atoms/Switch';
import TabText from '../Atoms/TabText';
import SecondaryActions from '../SecondaryActions';

import Action from './Action';
import Breadcrumb from './BreadCrumb';
import s from './Header.module.scss';

const Header: React.FC<{
  titleTruncate?: boolean;
  title?: ReactNode;
  description?: ReactNode;
  hierarchy?: 'h1' | 'h2' | 'h3';
  breadcrumbs?: Array<Parameters<typeof Breadcrumb>[0]['breadcrumb']>;
  tabs?: Array<Parameters<typeof TabText>[0]>;
  actions?: Array<Parameters<typeof Action>[0]['action'] | false | null | undefined | ''>;
  secondaryActions?: Array<Parameters<typeof Action>[0]['action'] | false | null | undefined | ''>;
  noDivider?: boolean;
  className?: string;
  isSticky?: boolean;
  hasSidebarTrigger?: boolean;
  Icon?: {
    Component: Icon;
    color?: string;
    onClick?: () => void;
  };
  Tag?: 'div' | 'summary';
  aiBg?: boolean;
  actionsComponent?: ReactNode;
  ariaLabel?: string;
  toggle?: {
    isChecked: boolean;
    label: string;
    onChange: (isChecked: boolean) => void;
  };
  topRightComponent?: ReactNode;
  tabsRightComponent?: ReactNode;
}> = ({
  className,
  breadcrumbs,
  title,
  description,
  hierarchy = 'h1',
  hasSidebarTrigger,
  tabs,
  actions: actionsProp,
  secondaryActions: secondaryActionsProp,
  topRightComponent,
  tabsRightComponent,
  noDivider,
  isSticky,
  Icon,
  Tag = 'div',
  aiBg,
  actionsComponent,
  ariaLabel,
  toggle,
  titleTruncate = false,
}) => {
  const actions = (actionsProp || []).filter(removeFalseyExcept0);
  const secondaryActions = (secondaryActionsProp || []).filter(removeFalseyExcept0);
  const toggleOnChangeRef = useRef(toggle?.onChange);
  toggleOnChangeRef.current = toggle?.onChange;
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const hasTabs = tabs && tabs.length > 0;
  const hasActions = actions.length > 0 || actionsComponent;
  const hasSecondaryActions = secondaryActions.length > 0;

  const router = useRouter();

  return (
    <Tag
      className={classNames(s.header, className)}
      style={{
        ...(isSticky ? { position: 'sticky', top: 0, zIndex: 2 } : {}),
        ...(noDivider ? { boxShadow: 'none', paddingBottom: 0, position: 'relative' } : {}),
        ...(aiBg ? { backgroundImage: 'var(--ai-bg-20)' } : {}),
      }}
    >
      {(hasBreadcrumbs || title || description || hasSecondaryActions) && (
        <div className={s.top}>
          {(hasBreadcrumbs || title || description) && (
            <div className={classNames(s.topContent, titleTruncate && [s.maxWidth])}>
              {hasBreadcrumbs && (
                <div className={s.breadcrumbs}>
                  {breadcrumbs.map((b, idx) => (
                    <Breadcrumb key={`breadcrumb-${b.label}`} breadcrumb={b} isLast={idx === breadcrumbs.length - 1} />
                  ))}
                </div>
              )}
              {(title || description) && (
                <div>
                  {aiBg && <NoodleAI size={40} />}
                  <h2
                    className={classNames(
                      titleTruncate && s.titleTruncate,
                      hierarchy === 'h1' && !hasSidebarTrigger && 'heading-sm',
                      (hierarchy === 'h2' || hasSidebarTrigger) && 'body-lg-bold',
                      !(hierarchy === 'h1' || hierarchy === 'h2') && 'body-md-bold',
                    )}
                  >
                    {hasSidebarTrigger && <SidebarTrigger className='mr-3' />}
                    {Tag === 'summary' && <CaretCircleDown weight='fill' />}
                    {Icon && (
                      <>
                        {Icon.onClick ? (
                          <Buttons isWrapper onClick={Icon.onClick}>
                            <Icon.Component size={hierarchy === 'h3' ? 20 : 24} weight='fill' color={Icon.color} />
                          </Buttons>
                        ) : (
                          <Icon.Component
                            size={hierarchy === 'h3' ? 20 : 24}
                            weight={Icon.color ? 'fill' : 'regular'}
                            color={Icon.color}
                          />
                        )}
                      </>
                    )}
                    {title}
                  </h2>
                  {typeof description === 'string' && (
                    <p className={classNames(s.description, hierarchy === 'h3' && 'caption')}>{description}</p>
                  )}
                  {typeof description === 'object' && description}
                </div>
              )}
            </div>
          )}
          {topRightComponent && <div className={s.topRightContainer}>{topRightComponent}</div>}
          {hasSecondaryActions && (
            <SecondaryActions
              ariaLabel={ariaLabel ?? 'Actions'}
              position='bottom-right'
              onSelect={(e) => {
                const selectedAction = secondaryActions.find((a) => a.label === e);
                if (selectedAction) {
                  if ('href' in selectedAction && selectedAction.href) {
                    router.push(selectedAction.href).catch(() => undefined);
                  } else if (selectedAction.onClick) {
                    selectedAction.onClick();
                  }
                }
              }}
              options={
                secondaryActions.map((a) => ({
                  destructive: a.isDestructive,
                  disabled: a.disabled,
                  icon: a.icon,
                  value: a.label,
                })) || []
              }
              className={s.secondaryActions}
            />
          )}
        </div>
      )}
      {toggle && (
        <Switch
          name='toggleSwitch'
          isChecked={toggle.isChecked}
          label={toggle.label}
          onChange={toggle.onChange}
          className={s.toggle}
        />
      )}
      {hasTabs && (
        <div className='flex items-center justify-between gap-3'>
          <div
            className={classNames(s.tabs, hasActions ? 'hide-scrollbar' : 'custom-scrollbar')}
            style={
              tabsRightComponent
                ? {
                    flex: 1,
                    margin: 0,
                    padding: 0,
                    width: 'auto',
                  }
                : undefined
            }
          >
            {tabs.map((t) => (
              <TabText
                key={`tab-${t.label}`}
                label={t.label}
                icon={t.icon}
                selected={t.selected}
                {...(t.onClick ? { onClick: t.onClick } : { href: t.href })}
              />
            ))}
          </div>
          {tabsRightComponent ? <div className='shrink-0'>{tabsRightComponent}</div> : null}
        </div>
      )}
      {hasActions && (
        <div className={s.actions}>
          {actionsComponent}
          {actions.map((a) => (
            <Action key={`action-${a.label}`} action={a} />
          ))}
        </div>
      )}
    </Tag>
  );
};

export default Header;
